// config/predefinedScreens.js
// Predefined screener presets. Easy to add new ones — just add an object with filters.
// Filter keys must match what the backend expects (same as filterData format).

export const predefinedScreens = [
    // ── Momentum & Technical ─────────────────────────────────────────────
    {
        id: 'overbought-us',
        name: 'Overbought (US)',
        filters: {
            rsiMoreThan: 70,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'oversold-us',
        name: 'Oversold (US)',
        filters: {
            rsiLowerThan: 30,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'high-beta-momentum',
        name: 'High Beta Momentum (US)',
        filters: {
            betaMoreThan: 1.5,
            rsiMoreThan: 60,
            marketCapMoreThan: 100000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'low-volatility',
        name: 'Low Volatility (US)',
        filters: {
            betaLowerThan: 0.8,
            marketCapMoreThan: 1000000000,
            rsiMoreThan: 30,
            rsiLowerThan: 70,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'large-cap-leaders',
        name: 'Large Cap Leaders (US)',
        filters: {
            marketCapMoreThan: 10000000000,
            averageType: '200',
            comparisonType: 'above',
            minPercentage: '0',
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },

    // ── Performance screens ──────────────────────────────────────
    {
        id: 'ytd-winners',
        name: 'YTD Winners (US)',
        filters: {
            perfYtdMoreThan: 50,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'ytd-losers',
        name: 'YTD Losers (US)',
        filters: {
            perfYtdLowerThan: -30,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'monthly-momentum',
        name: 'Monthly Momentum (US)',
        filters: {
            perf1MMoreThan: 15,
            perf5DMoreThan: 0,
            marketCapMoreThan: 500000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },

    // ── Value & Valuation ────────────────────────────────────────────────
    {
        id: 'undervalued-large-caps',
        name: 'Undervalued Large Caps (US)',
        filters: {
            dcfDiffMoreThan: 20,
            marketCapMoreThan: 1000000000,
            peLowerThan: 15,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'deep-value',
        name: 'Deep Value (US)',
        filters: {
            dcfDiffMoreThan: 50,
            peLowerThan: 10,
            priceLowerThan: 20,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'quality-value',
        name: 'Quality Value (US)',
        filters: {
            dcfDiffMoreThan: 15,
            betaLowerThan: 1.2,
            marketCapMoreThan: 500000000,
            peLowerThan: 20,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'mid-cap-opportunities',
        name: 'Mid Cap Opportunities (US)',
        filters: {
            marketCapMoreThan: 2000000000,
            marketCapLowerThan: 10000000000,
            dcfDiffMoreThan: 10,
            peLowerThan: 25,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },

    // ── Sector Plays ─────────────────────────────────────────────────────
    {
        id: 'tech-value',
        name: 'Tech Value Play (US)',
        filters: {
            sector: 'Technology',
            dcfDiffMoreThan: 15,
            peLowerThan: 30,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'defensive-dividend',
        name: 'Defensive Plays (US)',
        filters: {
            sector: 'Utilities',
            betaLowerThan: 1,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },

    // Analyst-driven screens ──────────────────────────────────────
    {
        id: 'analyst-favorites',
        name: 'Analyst Favorites (US)',
        filters: {
            consensus: ['Strong Buy'],
            strongBuyMoreThan: 5,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'analyst-buy-consensus',
        name: 'Buy Consensus (US)',
        filters: {
            consensus: ['Strong Buy', 'Buy'],
            marketCapMoreThan: 500000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'contrarian-sells',
        name: 'Contrarian - Analyst Sells (US)',
        filters: {
            consensus: ['Sell', 'Strong Sell'],
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },

    // Financial screens ───────────────────────────────────────────
    {
        id: 'revenue-giants',
        name: 'Revenue Giants (US)',
        filters: {
            revenueMoreThan: 10000000000,
            marketCapMoreThan: 5000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'profitable-growth',
        name: 'Profitable Growth (US)',
        filters: {
            revenueMoreThan: 1000000000,
            netIncomeMoreThan: 0,
            epsMoreThan: 2,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'high-ebitda',
        name: 'High EBITDA (US)',
        filters: {
            ebitdaMoreThan: 1000000000,
            marketCapMoreThan: 5000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'undervalued-by-analysts-and-dcf',
        name: 'Undervalued + Analyst Buy (US)',
        filters: {
            consensus: ['Strong Buy', 'Buy'],
            dcfDiffMoreThan: 20,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'cash-machines',
        name: 'Cash Machines (US)',
        filters: {
            freeCashFlowMoreThan: 1000000000,
            marketCapMoreThan: 10000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'big-buybacks',
        name: 'Big Buybacks (US)',
        filters: {
            commonStockRepurchasedLowerThan: -1000000000,
            marketCapMoreThan: 10000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'low-dilution-profitable',
        name: 'Low Dilution + Profitable (US)',
        filters: {
            stockBasedCompensationLowerThan: 100000000,
            freeCashFlowMoreThan: 0,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },

    // Rating & Score screens ──────────────────────────────────
    {
        id: 'top-rated-large',
        name: 'Top Rated Large Caps (US)',
        filters: {
            ratingRecommendation: ['Strong Buy', 'Buy'],
            marketCapMoreThan: 10000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'financially-strong',
        name: 'Financially Strong (US)',
        filters: {
            piotroskiScoreMoreThan: 7,
            altmanZScoreMoreThan: 3,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'bankruptcy-risk',
        name: 'Bankruptcy Risk (US)',
        filters: {
            altmanZScoreLowerThan: 1.8,
            marketCapMoreThan: 500000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    // ── Key Metrics & Ratios screens ─────────────────────────────
    {
        id: 'high-roe-quality',
        name: 'High ROE Quality (US)',
        filters: {
            returnOnEquityTTMMoreThan: 0.2,
            debtToEquityRatioTTMLowerThan: 1.5,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'cheap-on-ev-ebitda',
        name: 'Cheap on EV/EBITDA (US)',
        filters: {
            evToEBITDATTMMoreThan: 0,
            evToEBITDATTMLowerThan: 10,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'high-margin-business',
        name: 'High Margin Business (US)',
        filters: {
            grossProfitMarginTTMMoreThan: 0.6,
            netProfitMarginTTMMoreThan: 0.15,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'dividend-income',
        name: 'Dividend Income (US)',
        filters: {
            dividendYieldTTMMoreThan: 0.03,
            dividendPayoutRatioTTMLowerThan: 0.8,
            marketCapMoreThan: 2000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'undervalued-fair-value',
        name: 'Below Fair Value (US)',
        filters: {
            priceToFairValueTTMLowerThan: 0.8,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
    {
        id: 'low-debt-high-returns',
        name: 'Low Debt + High Returns (US)',
        filters: {
            debtToEquityRatioTTMLowerThan: 0.5,
            returnOnInvestedCapitalTTMMoreThan: 0.15,
            marketCapMoreThan: 1000000000,
            exchange: ['NASDAQ', 'NYSE', 'AMEX'],
        },
    },
];
